import { useState, useEffect } from "react";
import "./PGBusinessThreeAboutUs.scss";
import BusinessThreeWhyUs from "../businessThreeComponents/BusinessThreeWhyUs";
import { useDocument } from "../../../../firebase-files/hooks/useDocument";
import { useFirestore } from "../../../../firebase-files/hooks/useFirestore";
import { useAuthContext } from "../../../../firebase-files/hooks/useAuthContext";
import firebase from "firebase/app";
import "firebase/storage";
import { useNavigate } from "react-router-dom";
import { projectFirestore } from "../../../../firebase-config/config";

const EditButton = ({ onClick }) => (
  <button className="edit-btn" onClick={onClick} title="Edit">
    ✎
  </button>
);

const EditableBlock = ({
  heading,
  paragraph,
  headingField,
  paragraphField,
  updateDocument,
  dbAppTypeDocuments,
  isSuperAdmin,
  isAdmin
}) => {
  const [editing, setEditing] = useState(false);
  const [localHeading, setLocalHeading] = useState(heading || "");
  const [localParagraph, setLocalParagraph] = useState(paragraph || "");

  const saveChanges = async () => {
    setEditing(false);
    const updates = {};
    if (localHeading !== heading) updates[headingField] = localHeading;
    if (localParagraph !== paragraph) updates[paragraphField] = localParagraph;
    if (Object.keys(updates).length > 0) {
      await updateDocument("aboutUs", updates);
    }
  };

  // Determine if edit button should be shown for text
  const showTextEditButton = () => {
    if (dbAppTypeDocuments?.apptype === "lite") {
      return isSuperAdmin;
    } else if (dbAppTypeDocuments?.apptype === "pro") {
      return isSuperAdmin || isAdmin;
    }
    return false;
  };

  return (
    <div className="editable-block position-relative">
      {editing ? (
        <>
          <input
            type="text"
            className="form-control mb-2"
            value={localHeading}
            onChange={(e) => setLocalHeading(e.target.value)}
            placeholder="Heading"
            autoFocus
          />
          <textarea
            className="form-control mb-2"
            rows={3}
            value={localParagraph}
            onChange={(e) => setLocalParagraph(e.target.value)}
            placeholder="Paragraph"
          />
          <button className="btn btn-success me-2" onClick={saveChanges}>
            Save
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setEditing(false)}
          >
            Cancel
          </button>
        </>
      ) : (
        <div className="position-relative">
          <h3>{heading}</h3>
          <p>{paragraph}</p>
          {showTextEditButton() && <EditButton onClick={() => setEditing(true)} />}
        </div>
      )}
    </div>
  );
};

const PGBusinessThreeAboutUs = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [slideClass, setSlideClass] = useState("slide-in");
  const { document: aboutUsData } = useDocument("pages-data", "aboutUs");
  const { updateDocument } = useFirestore("pages-data");
  const { user } = useAuthContext();
  const isAdmin = user?.role === "admin";
  const isSuperAdmin = user && user.role === "superAdmin";
  const navigate = useNavigate();

  const { document: dbAppTypeDocuments } = useDocument("settings", "AppType");

  // --- Auto-create document if it doesn't exist ---
  useEffect(() => {
    const createDocumentIfNotExists = async () => {
      try {
        const docRef = projectFirestore.collection("pages-data").doc("aboutUs");
        const docSnap = await docRef.get();
        if (!docSnap.exists) {
          await docRef.set({
            theWhy: {
              firstHeading: "",
              firstParagraph: "",
              firstImage: "",
              secondHeading: "",
              secondParagraph: "",
              secondImage: "",
            },
            theWhat: {
              firstHeading: "",
              firstParagraph: "",
              firstImage: "",
              secondHeading: "",
              secondParagraph: "",
              secondImage: "",
            },
            theHow: {
              firstHeading: "",
              firstParagraph: "",
              firstImage: "",
              secondHeading: "",
              secondParagraph: "",
              secondImage: "",
            },
          });
        }
      } catch (err) {
        console.error("Error creating aboutUs document:", err);
      }
    };

    createDocumentIfNotExists();
  }, []);

  const services = ["The Why", "The What", "The How"];

  const handleTabClick = (index) => {
    if (index !== activeTab) {
      setSlideClass("slide-out");
      setTimeout(() => {
        setActiveTab(index);
        setSlideClass("slide-in");
      }, 500);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const storageRef = firebase
      .storage()
      .ref(`aboutUsImages/${Date.now()}_${file.name}`);
    const uploadTask = storageRef.put(file);

    uploadTask.on(
      "state_changed",
      null,
      (error) => console.error("Upload failed:", error),
      async () => {
        const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
        await updateDocument("aboutUs", { [field]: downloadURL });
      }
    );
  };

  return (
    <div style={{ backgroundColor: "white" }}>
      <div className="container">
        <br />
        <div className="business-three-about-us-heading">
          <h1>
            About <span style={{ color: "var(--click-color)" }}>Us</span>
          </h1>
        </div>
        <br />

        <div className="d-flex align-items-center justify-content-between business-three-about-top-selector">
          {services.map((service, index) => (
            <div
              key={index}
              onClick={() => handleTabClick(index)}
              className={`business-three-service-tab ${
                activeTab === index ? "active" : ""
              }`}
            >
              {service}
            </div>
          ))}
        </div>
        <br />

        {/* The Why Section */}
        {activeTab === 0 && (
          <div className={`about-the-why-section ${slideClass}`}>
            <div className="large_margin_div"></div>
            <div className="business-three-custom-row">
              <div className="business-three-custom-col position-relative">
                <EditableBlock
                  heading={aboutUsData?.theWhy?.firstHeading}
                  paragraph={aboutUsData?.theWhy?.firstParagraph}
                  headingField="theWhy.firstHeading"
                  paragraphField="theWhy.firstParagraph"
                  updateDocument={updateDocument}
                  dbAppTypeDocuments={dbAppTypeDocuments}
                  isSuperAdmin={isSuperAdmin}
                  isAdmin={isAdmin}
                />
              </div>
              <div className="business-three-custom-col position-relative editable-container">
                <img
                  src={
                    aboutUsData?.theWhy?.firstImage ||
                    "https://via.placeholder.com/600x400?text=First+Image"
                  }
                  alt="about us img"
                  className="img-fluid"
                />
                {dbAppTypeDocuments?.apptype === "lite" && isSuperAdmin && (
                  <>
                    <EditButton
                      onClick={() =>
                        document.getElementById("theWhy-firstImage").click()
                      }
                    />
                    <input
                      type="file"
                      id="theWhy-firstImage"
                      style={{ display: "none" }}
                      accept="image/*"
                      onChange={(e) =>
                        handleImageUpload(e, "theWhy.firstImage")
                      }
                    />
                  </>
                )}

                {dbAppTypeDocuments?.apptype === "pro" &&
                  (isSuperAdmin || isAdmin) && (
                    <>
                      <EditButton
                        onClick={() =>
                          document.getElementById("theWhy-firstImage").click()
                        }
                      />
                      <input
                        type="file"
                        id="theWhy-firstImage"
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={(e) =>
                          handleImageUpload(e, "theWhy.firstImage")
                        }
                      />
                    </>
                  )}
              </div>
            </div>
            <div className="large_margin_div"></div>
            <div className="business-three-custom-row business-three-about-us-reverse-sec">
              <div className="business-three-custom-col position-relative">
                <EditableBlock
                  heading={aboutUsData?.theWhy?.secondHeading}
                  paragraph={aboutUsData?.theWhy?.secondParagraph}
                  headingField="theWhy.secondHeading"
                  paragraphField="theWhy.secondParagraph"
                  updateDocument={updateDocument}
                  dbAppTypeDocuments={dbAppTypeDocuments}
                  isSuperAdmin={isSuperAdmin}
                  isAdmin={isAdmin}
                />
              </div>
              <div className="business-three-custom-col position-relative editable-container">
                <img
                  src={
                    aboutUsData?.theWhy?.secondImage ||
                    "https://via.placeholder.com/600x400?text=Second+Image"
                  }
                  alt="about us img"
                  className="img-fluid"
                />
                {dbAppTypeDocuments?.apptype === "lite" && isSuperAdmin && (
                  <>
                    <EditButton
                      onClick={() =>
                        document.getElementById("theWhy-secondImage").click()
                      }
                    />
                    <input
                      type="file"
                      id="theWhy-secondImage"
                      style={{ display: "none" }}
                      accept="image/*"
                      onChange={(e) =>
                        handleImageUpload(e, "theWhy.secondImage")
                      }
                    />
                  </>
                )}

                {dbAppTypeDocuments?.apptype === "pro" &&
                  (isSuperAdmin || isAdmin) && (
                    <>
                      <EditButton
                        onClick={() =>
                          document.getElementById("theWhy-secondImage").click()
                        }
                      />
                      <input
                        type="file"
                        id="theWhy-secondImage"
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={(e) =>
                          handleImageUpload(e, "theWhy.secondImage")
                        }
                      />
                    </>
                  )}
              </div>
            </div>
            <div className="large_margin_div"></div>
          </div>
        )}

        {/* The What Section */}
        {activeTab === 1 && (
          <div className={`about-the-what-section ${slideClass}`}>
            <div className="large_margin_div"></div>

            <div className="business-three-custom-row">
              <div className="business-three-custom-col position-relative">
                <EditableBlock
                  heading={aboutUsData?.theWhat?.firstHeading}
                  paragraph={aboutUsData?.theWhat?.firstParagraph}
                  headingField="theWhat.firstHeading"
                  paragraphField="theWhat.firstParagraph"
                  updateDocument={updateDocument}
                  dbAppTypeDocuments={dbAppTypeDocuments}
                  isSuperAdmin={isSuperAdmin}
                  isAdmin={isAdmin}
                />
              </div>
              <div className="business-three-custom-col position-relative editable-container">
                <img
                  src={
                    aboutUsData?.theWhat?.firstImage ||
                    "https://via.placeholder.com/600x400?text=First+Image"
                  }
                  alt="about us img"
                  className="img-fluid"
                />

                {dbAppTypeDocuments?.apptype === "lite" && isSuperAdmin && (
                  <>
                    <EditButton
                      onClick={() =>
                        document.getElementById("theWhat-firstImage").click()
                      }
                    />
                    <input
                      type="file"
                      id="theWhat-firstImage"
                      style={{ display: "none" }}
                      accept="image/*"
                      onChange={(e) =>
                        handleImageUpload(e, "theWhat.firstImage")
                      }
                    />
                  </>
                )}

                {dbAppTypeDocuments?.apptype === "pro" &&
                  (isSuperAdmin || isAdmin) && (
                    <>
                      <EditButton
                        onClick={() =>
                          document.getElementById("theWhat-firstImage").click()
                        }
                      />
                      <input
                        type="file"
                        id="theWhat-firstImage"
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={(e) =>
                          handleImageUpload(e, "theWhat.firstImage")
                        }
                      />
                    </>
                  )}
              </div>
            </div>

            <div className="large_margin_div"></div>
            <div className="business-three-custom-row business-three-about-us-reverse-sec">
              <div className="business-three-custom-col position-relative">
                <EditableBlock
                  heading={aboutUsData?.theWhat?.secondHeading}
                  paragraph={aboutUsData?.theWhat?.secondParagraph}
                  headingField="theWhat.secondHeading"
                  paragraphField="theWhat.secondParagraph"
                  updateDocument={updateDocument}
                  dbAppTypeDocuments={dbAppTypeDocuments}
                  isSuperAdmin={isSuperAdmin}
                  isAdmin={isAdmin}
                />
              </div>
              <div className="business-three-custom-col position-relative editable-container">
                <img
                  src={
                    aboutUsData?.theWhat?.secondImage ||
                    "https://via.placeholder.com/600x400?text=Second+Image"
                  }
                  alt="about us img"
                  className="img-fluid"
                />
                {dbAppTypeDocuments?.apptype === "lite" && isSuperAdmin && (
                  <>
                    <EditButton
                      onClick={() =>
                        document.getElementById("theWhat-secondImage").click()
                      }
                    />
                    <input
                      type="file"
                      id="theWhat-secondImage"
                      style={{ display: "none" }}
                      accept="image/*"
                      onChange={(e) =>
                        handleImageUpload(e, "theWhat.secondImage")
                      }
                    />
                  </>
                )}

                {dbAppTypeDocuments?.apptype === "pro" &&
                  (isSuperAdmin || isAdmin) && (
                    <>
                      <EditButton
                        onClick={() =>
                          document.getElementById("theWhat-secondImage").click()
                        }
                      />
                      <input
                        type="file"
                        id="theWhat-secondImage"
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={(e) =>
                          handleImageUpload(e, "theWhat.secondImage")
                        }
                      />
                    </>
                  )}
              </div>
            </div>

            <div className="large_margin_div"></div>
          </div>
        )}

        {/* The How Section */}
        {activeTab === 2 && (
          <div>
            <div className="large_margin_div"></div>

            <div
              className={`business-three-service-selected-service ${slideClass}`}
            >
              <div className="business-three-service-selected-service-content">
                <div className="business-three-custom-row">
                  <div className="business-three-custom-col position-relative">
                    <EditableBlock
                      heading={aboutUsData?.theHow?.firstHeading}
                      paragraph={aboutUsData?.theHow?.firstParagraph}
                      headingField="theHow.firstHeading"
                      paragraphField="theHow.firstParagraph"
                      updateDocument={updateDocument}
                      dbAppTypeDocuments={dbAppTypeDocuments}
                      isSuperAdmin={isSuperAdmin}
                      isAdmin={isAdmin}
                    />
                    <br />
                    {/* <button
                      className="btn_border"
                      onClick={() => navigate("/contact")}
                    >
                      Contact Us
                    </button> */}
                  </div>
                  <div className="business-three-custom-col d-flex justify-content-end position-relative editable-container">
                    <img
                      src={
                        aboutUsData?.theHow?.firstImage ||
                        "https://via.placeholder.com/600x400?text=First+Image"
                      }
                      alt="about us img"
                      className="img-fluid"
                    />
                    {dbAppTypeDocuments?.apptype === "lite" && isSuperAdmin && (
                      <>
                        <EditButton
                          onClick={() =>
                            document.getElementById("theHow-firstImage").click()
                          }
                        />
                        <input
                          type="file"
                          id="theHow-firstImage"
                          style={{ display: "none" }}
                          accept="image/*"
                          onChange={(e) =>
                            handleImageUpload(e, "theHow.firstImage")
                          }
                        />
                      </>
                    )}

                    {dbAppTypeDocuments?.apptype === "pro" &&
                      (isSuperAdmin || isAdmin) && (
                        <>
                          <EditButton
                            onClick={() =>
                              document
                                .getElementById("theHow-firstImage")
                                .click()
                            }
                          />
                          <input
                            type="file"
                            id="theHow-firstImage"
                            style={{ display: "none" }}
                            accept="image/*"
                            onChange={(e) =>
                              handleImageUpload(e, "theHow.firstImage")
                            }
                          />
                        </>
                      )}
                  </div>
                </div>
              </div>
            </div>

            <br />
            <br />

            <div
              className={`business-three-service-selected-service ${slideClass}`}
            >
              <div className="business-three-service-selected-service-content">
                <div className="business-three-custom-row business-three-service-selected-service-content-inside">
                  <div className="business-three-custom-col position-relative">
                    <EditableBlock
                      heading={aboutUsData?.theHow?.secondHeading}
                      paragraph={aboutUsData?.theHow?.secondParagraph}
                      headingField="theHow.secondHeading"
                      paragraphField="theHow.secondParagraph"
                      updateDocument={updateDocument}
                      dbAppTypeDocuments={dbAppTypeDocuments}
                      isSuperAdmin={isSuperAdmin}
                      isAdmin={isAdmin}
                    />
                    <br />
                    {/* <button
                      className="btn_border"
                      onClick={() => navigate("/contact")}
                    >
                      Contact Us
                    </button> */}
                  </div>
                  <div className="business-three-custom-col d-flex justify-content-end position-relative editable-container">
                    <img
                      src={
                        aboutUsData?.theHow?.secondImage ||
                        "https://via.placeholder.com/600x400?text=Second+Image"
                      }
                      alt="about us img"
                      className="img-fluid"
                    />
                    {dbAppTypeDocuments?.apptype === "lite" && isSuperAdmin && (
                      <>
                        <EditButton
                          onClick={() =>
                            document
                              .getElementById("theHow-secondImage")
                              .click()
                          }
                        />
                        <input
                          type="file"
                          id="theHow-secondImage"
                          style={{ display: "none" }}
                          accept="image/*"
                          onChange={(e) =>
                            handleImageUpload(e, "theHow.secondImage")
                          }
                        />
                      </>
                    )}

                    {dbAppTypeDocuments?.apptype === "pro" &&
                      (isSuperAdmin || isAdmin) && (
                        <>
                          <EditButton
                            onClick={() =>
                              document
                                .getElementById("theHow-secondImage")
                                .click()
                            }
                          />
                          <input
                            type="file"
                            id="theHow-secondImage"
                            style={{ display: "none" }}
                            accept="image/*"
                            onChange={(e) =>
                              handleImageUpload(e, "theHow.secondImage")
                            }
                          />
                        </>
                      )}
                  </div>
                </div>
              </div>
            </div>

            <div className="large_margin_div"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PGBusinessThreeAboutUs;